import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  TextField, Chip, Box, Typography
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import Autocomplete from '@mui/material/Autocomplete';
import Spinner from 'src/components/Spinner/Spinner';
import {
  getAllKeywordsList,
  clearKeywordsList,
  setKeywordsQuery,
} from 'src/actions/messagingSettingsActions';
import {
  setValuesFormExecuteKeyword,
} from 'src/actions/customerDetailsActions';
import { MESSAGING_SETTINGS } from 'src/constants';

import helpers from 'src/helpers';

function ExecuteKeywordModal({
  classes,
  loading,
  selectedKeyword = null,
  customer,
}) {
  const dispatch = useDispatch();
  const {
    keywordsList,
    loading: keywordsLoading,
  } = useSelector((state) => state?.messagingSettings || {});

  const {
    keywords: allKeywords,
  } = keywordsList || {};

  const availableKeywords = allKeywords?.filter((keyword) => keyword.status === 'Active') || [];

  const handleOnChange = (event, value) => {
    dispatch(setValuesFormExecuteKeyword({
      selectedKeyword: value,
    }));
  };

  // Custom render option for the dropdown
  const renderOption = (props, option) => (
    <Box
      component="li"
      {...props}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        py: 1.5,
        borderBottom: '1px solid #f0f0f0'
      }}
    >
      <Box sx={{ width: '100%', mb: 0.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          {option.name}
        </Typography>
      </Box>

      {!helpers.isEmpty(option.sendMessage) && (
        <Box sx={{ width: '100%', mb: 0.5 }}>
          <Typography variant="caption">
            <span style={{ fontWeight: 'bold' }}>Send Text:</span>
            <span style={{ color: '#666' }}>
              {option.sendMessage.substring(0, 80)}
              {option.sendMessage.length > 80 ? '...' : ''}
            </span>
          </Typography>
        </Box>
      )}

      {option.categoriesToAdd && option.categoriesToAdd.length > 0 && (
        <Box sx={{ width: '100%', mb: 0.5 }}>
          <Typography variant="caption" sx={{ mr: 1, fontWeight: 'bold' }}>Add Categories:</Typography>
          {option.categoriesToAdd.slice(0, 3).map((category) => (
            <Chip
              key={category}
              label={category}
              size="small"
              variant="outlined"
              sx={{
                mr: 0.5, mb: 0.5, height: 20, fontSize: '0.7rem'
              }}
              color="primary"
            />
          ))}
          {option.categoriesToAdd.length > 3 && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              +
              {option.categoriesToAdd.length - 3}
              {' '}
              more
            </Typography>
          )}
        </Box>
      )}

      {option.eventsToAdd && option.eventsToAdd.length > 0 && (
        <Box sx={{ width: '100%' }}>
          <Typography variant="caption" sx={{ mr: 1, fontWeight: 'bold' }}>Add to Events:</Typography>
          {option.eventsToAdd.slice(0, 2).map((event) => (
            <Chip
              key={event}
              label={event}
              size="small"
              variant="outlined"
              sx={{
                mr: 0.5, mb: 0.5, height: 20, fontSize: '0.7rem'
              }}
              color="secondary"
            />
          ))}
          {option.eventsToAdd.length > 2 && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              +
              {option.eventsToAdd.length - 2}
              {' '}
              more
            </Typography>
          )}
        </Box>
      )}

      {option.groupsToAdd && option.groupsToAdd.length > 0 && (
        <Box sx={{ width: '100%' }}>
          <Typography variant="caption" sx={{ mr: 1, fontWeight: 'bold' }}>Add to Groups:</Typography>
          {option.groupsToAdd.slice(0, 2).map((group) => (
            <Chip
              key={group}
              label={group}
              size="small"
              variant="outlined"
              sx={{
                mr: 0.5, mb: 0.5, height: 20, fontSize: '0.7rem'
              }}
              color="success"
            />
          ))}
          {option.groupsToAdd.length > 2 && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              +
              {option.groupsToAdd.length - 2}
              {' '}
              more
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );

  const getOptionLabel = (option) => {
    if (!option || !option.name) return '';
    return option.name;
  };

  useEffect(() => {
    dispatch(setKeywordsQuery({
      status: MESSAGING_SETTINGS.MESSAGING_SETTINGS_KEYWORDS_STATUS.ACTIVE,
    }));
    dispatch(getAllKeywordsList(true)); // Get all keywords without pagination

    return () => {
      dispatch(clearKeywordsList());
      dispatch(setKeywordsQuery(false));
      // Clear the selected keyword when modal unmounts
      dispatch(setValuesFormExecuteKeyword({
        selectedKeyword: null,
      }));
    };
  }, [dispatch]);

  return (
    <div className={classes.form}>
      <Spinner
        loading={loading || keywordsLoading}
        positionType="absolute"
      />
      <Grid
        container
        spacing={5}
        className={`${classes.groupLabelText} mt0 semiBold`}
      >
        <Grid
          xs={12}
          className="pt0"
        >
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>
              Select a keyword to execute for
              {' '}
              {customer?.firstName}
              {' '}
              {customer?.lastName}
              .
            </strong>
          </Typography>
          <Typography variant="body2">
            This will trigger the same actions that would occur if they texted the keyword to you.
          </Typography>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={8}
      >
        <Grid xs={12}>
          <Autocomplete
            renderInput={(params) => (
              <TextField
                label="Select a Keyword"
                fullWidth
                {...params}
              />
            )}
            value={selectedKeyword}
            options={availableKeywords}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={getOptionLabel}
            renderOption={renderOption}
            onChange={handleOnChange}
            disabled={loading || keywordsLoading}
            clearOnEscape
            ListboxProps={{
              style: {
                maxHeight: 400,
              },
            }}
          />
        </Grid>
      </Grid>
      {selectedKeyword && (
        <Grid
          container
          spacing={5}
          className={`${classes.groupLabelText} mt2`}
        >
          <Grid xs={12}>
            <Box>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Selected Keyword:</strong>
                {' '}
                {selectedKeyword.name}
              </Typography>

              {selectedKeyword.sendMessage && (
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Send Text:</strong>
                  {' '}
                  {helpers.isEmpty(selectedKeyword.sendMessage)
                    ? 'No text will be sent'
                    : (
                      <>
                        {selectedKeyword.sendMessage.substring(0, 200)}
                        {selectedKeyword.sendMessage.length > 200 ? '...' : ''}
                      </>
                    )}
                </Typography>
              )}

              {selectedKeyword.categoriesToAdd && selectedKeyword.categoriesToAdd.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body1" component="span" sx={{ mr: 1, fontWeight: 'bold' }}>
                    Add Categories:
                  </Typography>
                  <Box component="span">
                    {selectedKeyword.categoriesToAdd.map((category) => (
                      <Chip
                        key={category}
                        label={category}
                        size="small"
                        sx={{
                          mr: 0.5, mb: 0.5, height: 20, fontSize: '0.75rem'
                        }}
                        variant="outlined"
                        color="primary"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {selectedKeyword.eventsToAdd && selectedKeyword.eventsToAdd.length > 0 && (
                <Box>
                  <Typography variant="body1" component="span" sx={{ mr: 1, fontWeight: 'bold' }}>
                    Add to Events:
                  </Typography>
                  <Box component="span">
                    {selectedKeyword.eventsToAdd.map((event) => (
                      <Chip
                        key={event}
                        label={event}
                        size="small"
                        sx={{
                          mr: 0.5, mb: 0.5, height: 20, fontSize: '0.75rem'
                        }}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {selectedKeyword.groupsToAdd && selectedKeyword.groupsToAdd.length > 0 && (
                <Box>
                  <Typography variant="body1" component="span" sx={{ mr: 1, fontWeight: 'bold' }}>
                    Add to Groups:
                  </Typography>
                  <Box component="span">
                    {selectedKeyword.groupsToAdd.map((group) => (
                      <Chip
                        key={group}
                        label={group}
                        size="small"
                        sx={{
                          mr: 0.5, mb: 0.5, height: 20, fontSize: '0.75rem'
                        }}
                        variant="outlined"
                        color="success"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      )}
    </div>
  );
}

ExecuteKeywordModal.propTypes = {
  classes: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  selectedKeyword: PropTypes.object,
  customer: PropTypes.object.isRequired,
};

export default ExecuteKeywordModal;
